import { useEffect, useMemo, useState } from "react";
import {
  X,
  Calendar,
  Sparkle,
  CheckCircle,
  Warning,
  ArrowClockwise,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  RESULT_STATUS_META,
  groupResults,
  humanReason,
  listClassroomsForOrg,
  runBulkTuition,
} from "@/lib/bulkTuition";
import { centsToUsd, listBillingFamilies } from "@/lib/billing";

/**
 * Dialog panel used on /admin/billing for bulk monthly tuition generation.
 *
 * Flow:
 *   1. configure inputs (month, issue/due, optional filters, force toggle)
 *   2. click "Preview" → dry-run RPC (p_commit=false) → show summary
 *   3. click "Generate" → live RPC (p_commit=true) → show result groups
 *   4. close — parent (AdminBilling) reloads the family list.
 */
export default function BulkTuitionPanel({ onClose, onCompleted }) {
  const [cadence, setCadence] = useState("monthly");
  const [cycleMonth, setCycleMonth] = useState(firstOfThisMonth());
  const [cycleWeek, setCycleWeek] = useState(mondayOfThisWeek());
  const [issueDate, setIssueDate] = useState(todayStr());
  const [dueDate, setDueDate] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState([]);
  const [families, setFamilies] = useState([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState([]);
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState(null);      // rows
  const [running, setRunning] = useState(null);      // 'preview' | 'generate' | null
  const [finalResult, setFinalResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cls, fams] = await Promise.all([
          listClassroomsForOrg(),
          listBillingFamilies(),
        ]);
        setClassrooms(cls);
        setFamilies(fams);
      } catch (err) {
        toast.error(err.message || "Could not load filters");
      }
    })();
  }, []);

  const computedDueDate = useMemo(() => {
    if (dueDate) return dueDate;
    if (cadence === "weekly") {
      if (!cycleWeek) return null;
      const d = new Date(cycleWeek);
      d.setUTCDate(d.getUTCDate() + 7);
      return d.toISOString().slice(0, 10);
    }
    if (!cycleMonth) return null;
    const d = new Date(cycleMonth);
    d.setUTCDate(15);
    return d.toISOString().slice(0, 10);
  }, [cadence, cycleMonth, cycleWeek, dueDate]);

  const buildArgs = (commit) => ({
    cadence,
    cycleMonth: cadence === "monthly" ? cycleMonth : null,
    cycleWeek: cadence === "weekly" ? cycleWeek : null,
    issueDate,
    dueDate: computedDueDate,
    classroomIds: selectedClassroomIds,
    familyIds: selectedFamilyIds,
    force,
    commit,
  });

  const doPreview = async () => {
    setRunning("preview");
    setPreview(null);
    setFinalResult(null);
    try {
      const rows = await runBulkTuition(buildArgs(false));
      setPreview(rows);
    } catch (err) {
      toast.error(err.message || "Preview failed");
    } finally {
      setRunning(null);
    }
  };

  const doGenerate = async () => {
    const group = preview ? groupResults(preview) : null;
    const creating = (group?.wouldCreate.length || 0) + (group?.wouldRegenerate.length || 0);
    if (creating === 0) {
      toast.error("Nothing to generate — preview shows no eligible families.");
      return;
    }
    const msg = force
      ? `Create ${creating} invoice(s)? Existing live invoices for the cycle month will be voided first.`
      : `Create ${creating} invoice(s) as drafts?`;
    if (!window.confirm(msg)) return;
    setRunning("generate");
    try {
      const rows = await runBulkTuition(buildArgs(true));
      setFinalResult(rows);
      toast.success(`${groupResults(rows).created.length + groupResults(rows).regenerated.length} invoice(s) created`);
      onCompleted?.();
    } catch (err) {
      toast.error(err.message || "Generation failed");
    } finally {
      setRunning(null);
    }
  };

  const toggleIn = (setter, list, id) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const summary = useMemo(() => {
    const rows = finalResult || preview || [];
    if (rows.length === 0) return null;
    const g = groupResults(rows);
    return {
      total: rows.length,
      created: g.created.length,
      regenerated: g.regenerated.length,
      wouldCreate: g.wouldCreate.length,
      wouldRegenerate: g.wouldRegenerate.length,
      skipped: g.skipped.length,
      totalAmount: rows
        .filter((r) => ["created", "would_create", "voided_and_regenerated", "would_void_and_regenerate"].includes(r.status))
        .reduce((a, r) => a + (r.amount_cents || 0), 0),
    };
  }, [finalResult, preview]);

  return (
    <div
      data-testid="bulk-tuition-panel"
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-neutral-900/50 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="my-8 w-full max-w-3xl rounded-2xl border border-neutral-200 bg-white shadow-xl"
      >
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-neutral-100 p-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
              <Sparkle size={18} weight="duotone" />
            </span>
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
                Generate tuition invoices
              </h2>
              <p className="text-xs text-neutral-500">
                Create draft invoices for every eligible family in one run.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bulk-tuition-close"
            aria-label="Close"
            className="rounded-full p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900"
          >
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="space-y-5 p-5">
          {/* Cadence picker */}
          <section>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Cadence
            </p>
            <div
              data-testid="bulk-tuition-cadence-chips"
              className="inline-flex rounded-full border border-neutral-200 bg-white p-1"
            >
              {[
                { key: "monthly", label: "Monthly" },
                { key: "weekly",  label: "Weekly"  },
              ].map((c) => (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`bulk-tuition-cadence-${c.key}`}
                  data-active={cadence === c.key}
                  onClick={() => {
                    setCadence(c.key);
                    setPreview(null);
                    setFinalResult(null);
                    setDueDate(null);
                  }}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                    cadence === c.key
                      ? "bg-primary text-white shadow-sm"
                      : "text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {/* Inputs */}
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {cadence === "monthly" ? (
              <LabeledInput
                label="Billing month"
                testId="bulk-tuition-cycle-month"
                type="month"
                value={cycleMonth?.slice(0, 7) || ""}
                onChange={(v) => {
                  const iso = v ? `${v}-01` : "";
                  setCycleMonth(iso);
                }}
                icon={<Calendar size={14} weight="duotone" />}
              />
            ) : (
              <LabeledInput
                label="Week starting (Monday)"
                testId="bulk-tuition-cycle-week"
                type="date"
                value={cycleWeek || ""}
                onChange={setCycleWeek}
                icon={<Calendar size={14} weight="duotone" />}
              />
            )}
            <LabeledInput
              label="Issue date"
              testId="bulk-tuition-issue"
              type="date"
              value={issueDate || ""}
              onChange={setIssueDate}
            />
            <LabeledInput
              label="Due date"
              testId="bulk-tuition-due"
              type="date"
              value={(dueDate || computedDueDate) || ""}
              onChange={setDueDate}
              placeholder="Auto · 15th of month"
            />
          </section>

          <section className="rounded-2xl border border-neutral-200 bg-neutral-50/50 p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Filter classrooms
            </p>
            {classrooms.length === 0 ? (
              <p className="text-xs text-neutral-500">Loading classrooms…</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="bulk-tuition-classroom-chips">
                {classrooms.map((c) => {
                  const active = selectedClassroomIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`bulk-tuition-classroom-${c.id}`}
                      data-active={active}
                      onClick={() => toggleIn(setSelectedClassroomIds, selectedClassroomIds, c.id)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                        active
                          ? "border-primary bg-primary text-white"
                          : "border-neutral-200 bg-white text-neutral-700 hover:border-primary/40"
                      }`}
                    >
                      {c.name}
                      {c.monthly_tuition_cents != null && (
                        <span className={active ? "text-white/80" : "text-neutral-400"}>
                          · {centsToUsd(c.monthly_tuition_cents)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="mt-2 text-[11px] text-neutral-500">
              Leave empty to include all classrooms.
            </p>
          </section>

          {families.length > 0 && (
            <section className="rounded-2xl border border-neutral-200 bg-neutral-50/50 p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                Limit to specific families (optional)
              </p>
              <div className="flex flex-wrap gap-2" data-testid="bulk-tuition-family-chips">
                {families.map((bp) => {
                  const active = selectedFamilyIds.includes(bp.family_id);
                  return (
                    <button
                      key={bp.family_id}
                      type="button"
                      data-testid={`bulk-tuition-family-${bp.family_id}`}
                      data-active={active}
                      onClick={() => toggleIn(setSelectedFamilyIds, selectedFamilyIds, bp.family_id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                        active
                          ? "border-primary bg-primary text-white"
                          : "border-neutral-200 bg-white text-neutral-700 hover:border-primary/40"
                      }`}
                    >
                      {bp.family?.display_name || "—"}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <label
            data-testid="bulk-tuition-force-toggle"
            className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
          >
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#5A7E46]"
              data-testid="bulk-tuition-force"
            />
            <div className="min-w-0">
              <p className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
                Void and regenerate existing live invoices
              </p>
              <p className="mt-0.5 text-xs text-neutral-600">
                When off, families with an existing invoice for this cycle are
                skipped. When on, those invoices are <span className="font-semibold">voided</span>{" "}
                and replaced with a fresh draft.
              </p>
            </div>
          </label>

          {/* Results table */}
          {(preview || finalResult) && (
            <section data-testid="bulk-tuition-results" className="rounded-2xl border border-neutral-200 bg-white">
              {summary && (
                <div className="flex flex-wrap items-center gap-3 border-b border-neutral-100 p-4">
                  <SummaryChip
                    testId="bulk-tuition-summary-created"
                    label={finalResult ? "Created" : "Will create"}
                    value={finalResult ? summary.created : summary.wouldCreate}
                    tone="bg-primary/10 text-primary-hover"
                    Icon={CheckCircle}
                  />
                  <SummaryChip
                    testId="bulk-tuition-summary-regenerated"
                    label={finalResult ? "Regenerated" : "Will regenerate"}
                    value={finalResult ? summary.regenerated : summary.wouldRegenerate}
                    tone="bg-secondary/30 text-neutral-900"
                    Icon={ArrowClockwise}
                  />
                  <SummaryChip
                    testId="bulk-tuition-summary-skipped"
                    label="Skipped"
                    value={summary.skipped}
                    tone="bg-neutral-100 text-neutral-600"
                    Icon={Warning}
                  />
                  <div className="ml-auto text-right">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                      Total
                    </p>
                    <p className="font-heading text-base font-semibold text-neutral-900">
                      {centsToUsd(summary.totalAmount)}
                    </p>
                  </div>
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
                  <tr>
                    <th className="px-4 py-2">Family</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Children</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2">Detail</th>
                  </tr>
                </thead>
                <tbody data-testid="bulk-tuition-rows">
                  {(finalResult || preview || []).map((r) => (
                    <ResultRow key={r.family_id} row={r} final={!!finalResult} />
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>

        {/* Footer actions */}
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-100 p-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="bulk-tuition-cancel"
            className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
          >
            Close
          </button>
          <button
            type="button"
            onClick={doPreview}
            disabled={running != null || !cycleMonth}
            data-testid="bulk-tuition-preview"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-4 py-2 text-sm font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
          >
            {running === "preview" ? "Checking…" : "Preview"}
          </button>
          <button
            type="button"
            onClick={doGenerate}
            disabled={running != null || !preview || summary == null || summary.wouldCreate + summary.wouldRegenerate === 0}
            data-testid="bulk-tuition-generate"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover disabled:opacity-60"
          >
            {running === "generate" ? "Generating…" : "Generate drafts"}
            {running !== "generate" && <PaperPlaneTilt size={12} weight="bold" />}
          </button>
        </footer>
      </div>
    </div>
  );
}

function LabeledInput({ label, testId, type, value, onChange, icon, placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {icon}
        {label}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

function SummaryChip({ label, value, tone, Icon, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${tone}`}
    >
      <Icon size={14} weight="duotone" />
      {value} {label}
    </span>
  );
}

function ResultRow({ row, final }) {
  const meta = RESULT_STATUS_META[row.status] || RESULT_STATUS_META.skipped;
  const reason = humanReason(row.reason);
  const hasDiscount =
    row.sibling_discount_cents > 0 && row.sibling_discount_percent > 0;
  return (
    <tr
      data-testid={`bulk-tuition-row-${row.family_id}`}
      data-status={row.status}
      className="border-t border-neutral-100 align-top"
    >
      <td className="px-4 py-2.5">
        <p className="font-semibold text-neutral-900">{row.family_name}</p>
      </td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}
        >
          {meta.label}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right font-mono">{row.child_count}</td>
      <td className="px-4 py-2.5 text-right font-mono">
        {row.amount_cents ? centsToUsd(row.amount_cents) : "—"}
      </td>
      <td className="px-4 py-2.5 text-xs text-neutral-500">
        {reason ? (
          reason
        ) : hasDiscount ? (
          <span
            data-testid={`bulk-tuition-row-${row.family_id}-discount`}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover"
          >
            Sibling {row.sibling_discount_percent}% · −{centsToUsd(row.sibling_discount_cents)}
          </span>
        ) : final && row.invoice_id ? (
          "Draft created"
        ) : null}
      </td>
    </tr>
  );
}

function firstOfThisMonth() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

function mondayOfThisWeek() {
  // Postgres `date_trunc('week', d)` returns Monday-of-week; mirror that.
  const d = new Date();
  const dow = d.getUTCDay();                  // 0=Sun … 6=Sat
  const offset = (dow + 6) % 7;               // days since Monday
  const monday = new Date(Date.UTC(
    d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - offset
  ));
  return monday.toISOString().slice(0, 10);
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
